import Button from '@components/common/Button'
import Divider from '@components/common/Divider'
import { RAQ_FORM_TYPES, RAQ_PAGE_TYPE } from '@components/utils/constants'
import { ITopNavigationResponse } from '@customTypes/commonResponses'
import { steps } from '@customTypes/index'
import useStickyRaQButton from '@hooks/useStickyRaQButton'
import { ITag } from '@modules/requestAQuote/components/TripPreferences'
import { FiltersContext, IFiltersContext } from '@providers/FiltersProvider'
import useRequestQuote from 'hooks/useRequestQuote'
import { RaQFormType, RaQPageType } from 'pages/things-to-do/[...slug]'
import {
  Context,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { RequestQuoteForm } from '@components/requestQuote/RequestQuoteForm'
import RequestQuote from '@components/common/requestQuote'

export interface ITripPreferencesProps {
  raqPageType: RaQPageType
  accommodationTags: ITag[]
  autoPlacesTags: ITag[]
  importantTags: ITag[]
  handleRemoveTag: (tagData: ITag, tagType?: string) => void
  locations: Array<{
    id: number | string
    title: string
    type: string
    notRemovable?: boolean
  }>
  setAccommodationsToStay: Dispatch<SetStateAction<ITag[]>>
  setAutoPlaces: Dispatch<SetStateAction<ITag[]>>
  setImportantTags: Dispatch<SetStateAction<ITag[]>>
}

interface IRaQFullFormProps {
  raqFormType: RaQFormType
  data?: {
    id: number
    Title: string
    locations?: Array<{ Title: string }>
    type?: string
  }
  topNavigation: ITopNavigationResponse
  raqPageType: RaQPageType
}

const RaQFullForm = ({
  raqFormType,
  data,
  topNavigation,
  raqPageType,
}: IRaQFullFormProps): JSX.Element => {
  const { stepOne, stepTwo, stepThree, success } = steps
  const { syncFilterBar, searchManagement } = useContext<IFiltersContext>(
    FiltersContext as Context<IFiltersContext>,
  )

  const {
    isRequestOpen,
    isStepTwoOpen,
    isStepThreeOpen,
    isStepSuccessMobile,
    onRequestOpen,
    onStepOneClick,
    onStepTwoClick,
    onStepThreeClick,
    onRequestSubmit,
    syncRaQFields,
    initializeRequestQuote,
  } = useRequestQuote()

  const [autoPlaces, setAutoPlaces] = useState<ITag[]>([])
  const [accommodationsToStay, setAccommodationsToStay] = useState<ITag[]>([])
  const [importantTags, setImportantTags] = useState<ITag[]>([])

  syncRaQFields(setAccommodationsToStay, setAutoPlaces, setImportantTags)

  const isRaQButtonSticky = useStickyRaQButton()

  const handleRemoveTag = useCallback(
    (tagData: ITag) => {
      const filterOptions = JSON.parse(
        localStorage.getItem('USER_SEARCH_FILTERS') ?? '{}',
      )

      setImportantTags((prevState) => {
        return prevState.filter(
          (entry: ITag) =>
            entry.title !== (tagData.title ? tagData.title : tagData.Title),
        )
      })

      if (filterOptions) {
        localStorage.setItem(
          'USER_SEARCH_FILTERS',
          JSON.stringify({
            ...filterOptions,
            experiences: filterOptions.experiences.filter(
              (entry: ITag) =>
                entry.title !== (tagData.title ? tagData.title : tagData.Title),
            ),
            locationTypes: filterOptions.locationTypes.filter(
              (entry: ITag) =>
                entry.title !== (tagData.title ? tagData.title : tagData.Title),
            ),
          }),
        )
      }

      const searchOptions = JSON.parse(
        localStorage.getItem('USER_SEARCH_QUERY_PARAMS') ?? '{}',
      )

      if (searchOptions) {
        localStorage.setItem(
          'USER_SEARCH_QUERY_PARAMS',
          JSON.stringify({
            ...searchOptions,
            tags:
              searchOptions?.tags?.filter(
                (tag: ITag) =>
                  tag.title !== (tagData.title ? tagData.title : tagData.Title),
              ) || [],
          }),
        )
      }
      syncFilterBar()
    },
    [
      setAccommodationsToStay,
      setAutoPlaces,
      setImportantTags,
      searchManagement,
    ],
  )

  const isOnPackagePage = useMemo(() => {
    return raqPageType === RAQ_PAGE_TYPE.PACKAGE
  }, [raqPageType])

  const tripPreferencesProps: ITripPreferencesProps = useMemo(() => {
    return {
      raqPageType,
      accommodationTags:
        data &&
        raqPageType === RAQ_PAGE_TYPE.ACCOMMODATION &&
        !accommodationsToStay.some((entry) => entry.title === data.Title)
          ? [
              {
                id: data.id,
                title: data.Title,
                notRemovable: true,
              },
              ...accommodationsToStay,
            ]
          : accommodationsToStay,
      autoPlacesTags:
        data &&
        raqPageType === RAQ_PAGE_TYPE.ACTIVITY &&
        !autoPlaces.some((entry) => entry.title === data.Title)
          ? [
              {
                id: data.id,
                title: data.Title,
                notRemovable: true,
              },
              ...autoPlaces,
            ]
          : autoPlaces,
      importantTags:
        data && raqPageType === RAQ_PAGE_TYPE.LANDING_TAG
          ? [
              {
                id: data.id,
                title: data.Title,
                type: data.type,
                notRemovable: true,
              },
              ...importantTags,
            ]
          : importantTags,
      handleRemoveTag,
      locations:
        isOnPackagePage && data
          ? data?.locations
            ? data.locations.map((location, index) => {
                return {
                  id: index,
                  title: location.Title,
                  type: 'Location',
                  notRemovable: true,
                }
              })
            : [
                {
                  id: '0',
                  title: data.Title,
                  type: 'Location',
                  notRemovable: true,
                },
              ]
          : data &&
              (raqPageType === RAQ_PAGE_TYPE.LANDING_LOCATION ||
                raqPageType === RAQ_PAGE_TYPE.LANDING_REGION)
            ? [
                {
                  id: data.id,
                  title: data.Title,
                  type: 'Location',
                  notRemovable: true,
                },
              ]
            : [],
      setAccommodationsToStay,
      setAutoPlaces,
      setImportantTags,
    }
  }, [
    accommodationsToStay,
    autoPlaces,
    importantTags,
    isOnPackagePage,
    handleRemoveTag,
    data,
    raqPageType,
  ])

  return (
    <div className='flex flex-col justify-center desktop:flex-row desktop:min-w-[39.16%]'>
      <div className={`sticky self-start top-[5.225rem] w-full`}>
        <RequestQuoteForm
          onRequestOpen={onRequestOpen}
          packageData={data}
          initializeRequestQuote={() =>
            initializeRequestQuote(
              tripPreferencesProps.accommodationTags,
              tripPreferencesProps.autoPlacesTags,
              tripPreferencesProps.importantTags,
              data,
            )
          }
          raqFormType={raqFormType}
          phone={{
            phoneAction: `tel:${topNavigation?.Phone_Action}`,
            phoneNumber: topNavigation?.Phone_Visible,
          }}
          tripPreferencesProps={tripPreferencesProps}
        />
      </div>
      {isRequestOpen && <Divider variant='short' />}

      {/* Long Description - data from strapi to be added later */}
      {/* <div className=" mt-16 desktop:block desktop:max-w-[750px] ">
        <div className="w-[100%] mb-12 desktop:mb-24">
          <p className="text-darkgray text-p"> */}
      {/* {applyHStyles(data.Long_Description)} */}
      {/* </p>
        </div>
      </div> */}

      {isRequestOpen && (
        <RequestQuote
          onStepOneClick={onStepOneClick}
          onRequestOpen={onRequestOpen}
          onStepTwoClick={onStepTwoClick}
          onStepThreeClick={onStepThreeClick}
          onRequestSubmit={onRequestSubmit}
          step={
            isRequestOpen &&
            !isStepTwoOpen &&
            !isStepThreeOpen &&
            !isStepSuccessMobile
              ? stepOne
              : isStepTwoOpen
                ? stepTwo
                : isStepThreeOpen
                  ? stepThree
                  : isStepSuccessMobile
                    ? success
                    : ''
          }
          topNavigation={topNavigation}
          packageData={data}
          raqFormType={raqFormType}
          tripPreferencesProps={tripPreferencesProps}
        />
      )}

      <div className='block desktop:hidden'>
        {!isRaQButtonSticky && (
          <div className='h-[5.5rem] desktop:hidden'>
            {/* This DIV is button placeholder for mobile hence the hardcoded height of the button */}
          </div>
        )}
        <div
          className={`${
            !isRaQButtonSticky ? 'fixed' : ''
          } bottom-0 right-0 z-10 w-full bg-white p-[10px]`}
        >
          <div className='mx-auto max-w-[21.875rem]'>
            <Button
              id={'initiate-mobile-raq'}
              size={'medium'}
              onClick={() => {
                if (raqFormType !== RAQ_FORM_TYPES.FORM_B) {
                  initializeRequestQuote(
                    tripPreferencesProps.accommodationTags,
                    tripPreferencesProps.autoPlacesTags,
                    tripPreferencesProps.importantTags,
                    data,
                  )
                }
                onRequestOpen(true)
              }}
            >
              Request a Custom Quote
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RaQFullForm
